import {StyleSheet} from "aphrodite";
import * as React from "react";

import {
    DesktopKeypad,
    MathInputI18nContextProvider,
} from "@khanacademy/math-input";
import {type PropsFor} from "@khanacademy/wonder-blocks-core";
import {PopoverContentCore} from "@khanacademy/wonder-blocks-popover";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {mathInputStrings} from "@khan/component-perseus";
import * as MobileApp from "@khan/mobile-app-integration";
import {RequestInfo} from "@khan/shared-core";

type DesktopKeypadProps = PropsFor<typeof DesktopKeypad>;

interface Props {
    /* Event handler for the onClickKey keypad event. */
    onClickKey: DesktopKeypadProps["onClickKey"];

    /* Event handler for analytics events. */
    onAnalyticsEvent: DesktopKeypadProps["onAnalyticsEvent"];

    /* Cursor context for the keypad. */
    cursorContext: DesktopKeypadProps["cursorContext"];

    /* Callback that will close the keypad when invoked. */
    closeKeypad: () => void;
}

/**
 * Render the keypad as popover content.
 *
 * This handles the mobile app integration for the keypad.
 */
export const MathEditorKeypad = React.forwardRef(
    (
        {onClickKey, onAnalyticsEvent, cursorContext, closeKeypad}: Props,
        ref: React.ForwardedRef<PopoverContentCore>,
    ) => {
        // Make sure the mobile app knows when the keypad is open and
        // can close it as needed.
        MobileApp.Guide.useUXStack("math-input", {onClose: closeKeypad});

        return (
            <PopoverContentCore
                ref={ref}
                closeButtonVisible
                style={styles.popoverContentCore}
            >
                <MathInputI18nContextProvider
                    locale={RequestInfo.kaLocale}
                    strings={mathInputStrings()}
                >
                    {" "}
                    <DesktopKeypad
                        extraKeys={["x", "y", "PI", "THETA"]}
                        onClickKey={onClickKey}
                        onAnalyticsEvent={onAnalyticsEvent}
                        cursorContext={cursorContext}
                        advancedRelations
                        basicRelations
                        divisionKey
                        logarithms
                        convertDotToTimes
                        preAlgebra
                        trigonometry
                    />
                </MathInputI18nContextProvider>
            </PopoverContentCore>
        );
    },
);

const styles = StyleSheet.create({
    popoverContentCore: {
        padding: 0,
        paddingBlockEnd: spacing.xxSmall_6,
        maxWidth: "initial",
    },
});
